// Graviton Design Tokens — derived from the Graviton Brand Guidelines.
// Palette (digital/HEX): Deep Blue #00102C (gradient stop #00225D),
// Neuro Blue 500 #0052E2 / 600 #0043B8 (gradient stop #002B76),
// Clarity 200 #E8EEF9 / 300 #BBCAE4 / 400 #708CBC, White.
// Usage rules honored here: light backgrounds use White/Clarity; dark backgrounds
// use Deep Blue(gradient) only; punchy Neuro Blue is reserved for marks/buttons
// with white text (contrast >= 8.4 per the Accessibility matrix).
// Structure mirrors ./swiish (base) — we clone and override so future base keys
// are inherited rather than silently lost.

const tailwindColors = require('tailwindcss/colors');

const GRAVITON = {
  DEEP_BLUE: '#00102C',
  DEEP_BLUE_GRAD: '#00225D',
  NEURO_500: '#0052E2',
  NEURO_600: '#0043B8',
  NEURO_600_GRAD: '#002B76',
  CLARITY_200: '#E8EEF9',
  CLARITY_300: '#BBCAE4',
  CLARITY_400: '#708CBC',
  WHITE: '#FFFFFF',
};

const base = require('./swiish');

const pair = (light, dark) => ({ light, dark });

const themed = JSON.parse(JSON.stringify(base));
const c = themed.colors;

// Accessibility matrix (Graviton guidelines, "Accessibility" page): on a given
// solid background only the listed foregrounds may carry text/content —
//   Deep Blue (#00102C)      -> White, Clarity 200, Clarity 300
//   Deep Blue gradient stop  -> White, Clarity 200 only
//   Clarity 200/300          -> Neuro 600 / Deep Blue
//   Neuro 500/600            -> White (and Clarity 200 on 600)
// The dark UI surfaces use Deep Blue (main) and the gradient stop (card/surface),
// so ALL dark foreground tokens below are White or Clarity 200/300 only — no
// Neuro Blue text on dark, no off-palette navy tints.
Object.assign(c, {
  main:           pair(GRAVITON.CLARITY_200, GRAVITON.DEEP_BLUE),
  card:           pair(GRAVITON.WHITE,       GRAVITON.DEEP_BLUE_GRAD),
  surface:        pair(GRAVITON.CLARITY_300, GRAVITON.DEEP_BLUE_GRAD),

  action:         pair(GRAVITON.NEURO_600,   GRAVITON.NEURO_500), // white text on both: 8.42 / 6.35 contrast
  actionHover:    pair(GRAVITON.NEURO_600_GRAD, GRAVITON.NEURO_600),

  textPrimary:    pair(GRAVITON.DEEP_BLUE,   GRAVITON.WHITE),
  textSecondary:  pair(GRAVITON.DEEP_BLUE,   GRAVITON.CLARITY_200), // 16.24 on Deep Blue/gradient
  textMuted:      pair(GRAVITON.DEEP_BLUE,   GRAVITON.CLARITY_200), // guidelines: no gray tints, no Clarity-400 body text on White
  textMutedSubtle:pair(GRAVITON.DEEP_BLUE,   GRAVITON.CLARITY_300), // subtle text: solid Deep Blue backgrounds only (11.42)

  border:         pair(GRAVITON.CLARITY_300, GRAVITON.DEEP_BLUE_GRAD),
  borderSubtle:   pair(GRAVITON.CLARITY_200, GRAVITON.DEEP_BLUE),

  link:           pair(GRAVITON.NEURO_600,   GRAVITON.CLARITY_200), // never Neuro on dark surfaces
  linkBg:         pair(GRAVITON.CLARITY_200, 'rgba(232, 238, 249, 0.12)'),
  linkBorder:     pair(GRAVITON.CLARITY_300, GRAVITON.CLARITY_200),
  linkHover:      pair(GRAVITON.CLARITY_300, GRAVITON.DEEP_BLUE),

  info:           pair(GRAVITON.NEURO_600,   GRAVITON.NEURO_500),
  infoHover:      pair(GRAVITON.NEURO_600_GRAD, GRAVITON.NEURO_600),
  infoBg:         pair(GRAVITON.CLARITY_200, 'rgba(232, 238, 249, 0.12)'),
  infoText:       pair(GRAVITON.NEURO_600,   GRAVITON.CLARITY_200),
  infoBorder:     pair(GRAVITON.CLARITY_300, GRAVITON.CLARITY_200),

  // WhatsApp/positive CTA: the palette has no green — Neuro Blue action colors
  // (white text 6.35) keep the matrix intact on both card surfaces.
  success:        pair(GRAVITON.NEURO_500,   GRAVITON.NEURO_500),
  successHover:   pair(GRAVITON.NEURO_600,   GRAVITON.NEURO_600),
  successText:    pair(GRAVITON.NEURO_600,   GRAVITON.CLARITY_200),
  successBg:      pair(GRAVITON.CLARITY_200, 'rgba(232, 238, 249, 0.12)'),
  successBorder:  pair(GRAVITON.CLARITY_300, GRAVITON.CLARITY_200),

  inputBg:        pair(GRAVITON.WHITE,       GRAVITON.DEEP_BLUE),
  inputBorder:    pair(GRAVITON.CLARITY_300, GRAVITON.CLARITY_200),
  inputText:      pair(GRAVITON.DEEP_BLUE,   GRAVITON.WHITE),

  focusRing:      pair(GRAVITON.NEURO_500,   GRAVITON.CLARITY_200),

  confirm:        pair(GRAVITON.CLARITY_300, GRAVITON.DEEP_BLUE), // dark: Deep Blue chip on gradient cards, white text
  confirmHover:   pair(GRAVITON.CLARITY_400, GRAVITON.DEEP_BLUE_GRAD),
  confirmText:    pair(GRAVITON.DEEP_BLUE,   GRAVITON.WHITE),
  confirmBg:      pair(GRAVITON.CLARITY_200, 'rgba(232, 238, 249, 0.12)'),
  confirmBorder:  pair(GRAVITON.CLARITY_300, GRAVITON.CLARITY_200),
});

// Guidelines favor flat/gradient fields — no grain textures.
themed.textures = {
  main:    { light: '', dark: '', size: '', blendLight: 'normal', blendDark: 'normal', opacityLight: 0, opacityDark: 0 },
  surface: { light: '', dark: '' },
  card:    { light: '', dark: '' },
};

module.exports = themed;
